import { DataTable, DataTableMessageRow } from 'components/DataTable'
import { Pill } from 'components/ManagementCard'
import { adCodeStrings, commonStrings } from 'constants/strings'
import type { TAdCodeListItem } from 'types'
import { AdCodeActions, AdCodeEditButton } from './AdCodeActions'
import {
  formatCount,
  formatEstimated,
  formatRate,
  mediumLabel,
  statusLabel,
} from './adCodeDisplay'

type Props = {
  adCodes: TAdCodeListItem[]
  loading: boolean
  emptyMessage: string
  onOpen: (adCodeId: number) => void
  /** コピー完了の通知。コピー自体は AdCodeCopyUrlButton が行う。 */
  onCopyUrl: () => void
  onEdit: (adCodeId: number) => void
  onToggleArchive: (adCode: TAdCodeListItem) => void
  archivingId?: number
}

const COLUMN_COUNT = 10

export function AdCodesTable({
  adCodes,
  loading,
  emptyMessage,
  onOpen,
  onCopyUrl,
  onEdit,
  onToggleArchive,
  archivingId,
}: Props) {
  let body: JSX.Element | JSX.Element[]

  if (loading) {
    body = (
      <DataTableMessageRow
        colSpan={COLUMN_COUNT}
        message={commonStrings.loading}
      />
    )
  } else if (adCodes.length === 0) {
    body = <DataTableMessageRow colSpan={COLUMN_COUNT} message={emptyMessage} />
  } else {
    body = adCodes.map((adCode) => {
      const estimated = formatEstimated(adCode.metrics)
      return (
        <tr
          key={adCode.id}
          className="cursor-pointer border-t border-border/60 bg-white hover:bg-surface/60"
          onClick={() => onOpen(adCode.id)}
        >
          <td className="px-2 py-3">
            <AdCodeEditButton adCodeId={adCode.id} onEdit={onEdit} />
          </td>
          <td className="px-2 py-3">
            <div className="text-sm text-ink">{adCode.name}</div>
            <div className="text-[11px] text-muted">
              {mediumLabel(adCode.medium)}
            </div>
          </td>
          <td className="px-2 py-3 font-mono text-[11px] text-ink sm:text-xs">
            {adCode.code}
          </td>
          <td className="px-2 py-3 text-right text-[11px] tabular-nums text-ink sm:text-sm">
            {formatCount(adCode.metrics.clickCount)}
          </td>
          <td className="px-2 py-3 text-right text-[11px] tabular-nums text-ink sm:text-sm">
            <div>{formatCount(adCode.metrics.confirmedCount)}</div>
            {estimated && (
              <div className="text-[10px] text-amber-700">{estimated}</div>
            )}
          </td>
          <td className="px-2 py-3 text-right text-[11px] tabular-nums text-ink sm:text-sm">
            {formatRate(adCode.metrics.registrationRate)}
          </td>
          <td className="px-2 py-3 text-right text-[11px] tabular-nums text-ink sm:text-sm">
            {formatCount(adCode.metrics.blocked24hCount)}
          </td>
          <td className="px-2 py-3 text-right text-[11px] tabular-nums text-ink sm:text-sm">
            {formatCount(adCode.metrics.blockedCount)}
          </td>
          <td className="px-2 py-3">
            <Pill tone={adCode.status === 'ACTIVE' ? 'success' : 'neutral'}>
              {statusLabel(adCode.status)}
            </Pill>
          </td>
          <td className="px-2 py-3">
            <AdCodeActions
              adCode={adCode}
              onCopyUrl={onCopyUrl}
              onToggleArchive={onToggleArchive}
              archivingId={archivingId}
            />
          </td>
        </tr>
      )
    })
  }

  return (
    <DataTable
      minWidthClassName="min-w-[1000px]"
      tableLayout="auto"
      headerRow={
        <tr>
          <th className="px-2 py-3 text-left">
            {adCodeStrings.detail.edit}
          </th>
          <th className="px-2 py-3 text-left">{adCodeStrings.columns.name}</th>
          <th className="px-2 py-3 text-left">{adCodeStrings.columns.code}</th>
          <th className="px-2 py-3 text-right">
            {adCodeStrings.metrics.click}
          </th>
          <th className="px-2 py-3 text-right">
            {adCodeStrings.metrics.registration}
          </th>
          <th className="px-2 py-3 text-right">
            {adCodeStrings.metrics.registrationRate}
          </th>
          <th className="px-2 py-3 text-right">
            {adCodeStrings.metrics.blocked24h}
          </th>
          <th className="px-2 py-3 text-right">
            {adCodeStrings.metrics.blocked}
          </th>
          <th className="px-2 py-3 text-left">
            {adCodeStrings.columns.status}
          </th>
          <th className="px-2 py-3 text-right">
            {adCodeStrings.columns.actions}
          </th>
        </tr>
      }
    >
      {body}
    </DataTable>
  )
}
